import { z } from 'zod';

import { apiRequest, apiRequestBlob, type RequestOptions } from '@/api/client';
import { pageSchema } from '@/api/types';
import { orderRequestSchema, type RequestKind } from '@/features/orders/api/requests';
import { moneyText, productSummarySchema } from '@/features/storefront/api/storefront';
import { focalPointSchema } from '@/features/storefront/sections/schema';
import { resolveStorefrontHost } from '@/features/storefront/storefrontHost';
import { storefrontLanguageHeaders } from '@/features/storefront/storefrontLanguageHeader';

/**
 * The signed-in shopper's own account.
 *
 * Every call here carries two things the public storefront calls do not: the
 * shopper's access token (`auth: 'customer'`, kept apart from the merchant
 * dashboard's session) and the same `X-Storefront-Host` the rest of the
 * storefront sends, because the server still resolves the shop from the
 * hostname before it will look at who is asking.
 */
function options(extra: RequestOptions = {}): RequestOptions {
  const host = resolveStorefrontHost();
  return {
    ...extra,
    auth: 'customer',
    headers: {
      ...(host ? { 'X-Storefront-Host': host } : {}),
      ...storefrontLanguageHeaders(),
      ...extra.headers,
    },
  };
}

export const customerProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  marketing_opt_in: z.boolean(),
  email_verified: z.boolean(),
  total_orders: z.number(),
  total_spent: z.string(),
  created_at: z.string(),
  // Absent on a server that predates multi-language (docs/i18n.md §7).
  preferred_language: z.string().nullable().optional(),
});

export const customerSessionSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  customer: customerProfileSchema,
});

export const registerResultSchema = z.object({ requires_email_verification: z.boolean() });

export const messageSchema = z.object({ message: z.string() });

export const activeSessionSchema = z.object({
  id: z.string(),
  device: z.string(),
  browser: z.string().nullable(),
  platform: z.string().nullable(),
  is_mobile: z.boolean(),
  ip_address: z.string().nullable(),
  last_active_at: z.string(),
  expires_at: z.string(),
  current: z.boolean(),
});

export const sessionRevocationSchema = z.object({
  revoked: z.number(),
  signed_out_here: z.boolean(),
});

export const savedAddressSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
  is_default: z.boolean(),
});

export const accountOrderSummarySchema = z.object({
  id: z.string(),
  order_number: z.string(),
  status: z.string(),
  payment_status: z.string(),
  payment_method: z.string(),
  total: z.string(),
  item_count: z.number(),
  created_at: z.string(),
  // Whether the order has a GST invoice to download. Optional so an older
  // server's order still parses (and simply offers no download).
  invoice_available: z.boolean().optional(),
  /** Present on the shopper's own online order while it still awaits payment. */
  pay_token: z.string().nullish(),
});

export const accountOrderDetailSchema = accountOrderSummarySchema.extend({
  email: z.string(),
  phone: z.string().nullable(),
  shipping_address: z.record(z.unknown()),
  subtotal: z.string(),
  discount_amount: z.string(),
  // The bundle's share of `discount_amount`, already inside it.
  bundle_discount_amount: z.string().default('0.00'),
  discount_code: z.string().nullable(),
  tax_amount: z.string(),
  shipping_amount: z.string(),
  // What choosing the payment method cost - a cash-on-delivery fee. Already in
  // `total`; optional so an older server's order still parses.
  payment_fee_amount: z.string().optional(),
  gift_card_amount: z.string(),
  amount_due: z.string(),
  customer_note: z.string().nullable(),
  items: z.array(
    z.object({
      // The line's id, which a return or replacement request names. Optional
      // so an older server's order still parses (and simply offers nothing).
      id: z.string().nullish(),
      product_name: z.string(),
      variant_options: z.record(z.unknown()),
      sku: z.string(),
      quantity: z.number(),
      unit_price: z.string(),
      line_subtotal: z.string(),
      variant_id: z.string().nullable(),
      product_slug: z.string().nullable(),
      image_url: z.string().nullable(),
      // The thumbnail's focal point, read live with the picture; null is the
      // middle, and so is an older server that does not send it.
      image_focal: focalPointSchema.nullish(),
      cancelled_quantity: z.number().default(0),
      returned_quantity: z.number().default(0),
      replaced_quantity: z.number().default(0),
      requestable_quantity: z.number().default(0),
      available_requests: z.array(z.string()).default([]),
    }),
  ),
  timeline: z.array(z.object({ status: z.string(), at: z.string() })).default([]),
  shipments: z
    .array(
      z.object({
        carrier: z.string().nullable(),
        tracking_number: z.string().nullable(),
        tracking_url: z.string().nullable(),
        status: z.string(),
        shipped_at: z.string().nullable(),
        delivered_at: z.string().nullable(),
        // The courier's promise and its trail, from the shipping module.
        // Defaulted so an order served before it existed still parses.
        estimated_delivery: z.string().nullish().transform((value) => value ?? null),
        events: z
          .array(
            z.object({
              status: z.string(),
              description: z.string().nullish().transform((value) => value ?? null),
              location: z.string().nullish().transform((value) => value ?? null),
              occurred_at: z.string(),
            }),
          )
          .default([]),
      }),
    )
    .default([]),
  can_cancel: z.boolean(),
  cancellation_reason: z.string().nullish(),
  cancelled_by: z.enum(['CUSTOMER', 'MERCHANT', 'SYSTEM']).nullish().catch(null),
  requests: z.array(orderRequestSchema).default([]),
  after_sale: z
    .object({
      can_cancel_partially: z.boolean().default(false),
      cancellation_reasons: z.array(z.string()).default([]),
      return_reasons: z.array(z.string()).default([]),
      policy_note: z.string().nullish(),
      allow_photos: z.boolean().default(true),
      request_deadline: z.string().nullish(),
    })
    .default({}),
});

export const reorderResultSchema = z.object({
  cart_token: z.string(),
  added: z.number(),
  unavailable: z.array(z.string()),
});

/**
 * One of the shopper's own appointments - what the confirmation email said,
 * and whether it can still be called off. See `AccountAppointment` on the server.
 */
export const accountAppointmentSchema = z.object({
  id: z.string(),
  service_name: z.string(),
  starts_at: z.string(),
  ends_at: z.string(),
  duration_minutes: z.number(),
  status: z.string(),
  team_member_name: z.string().nullable().default(null),
  location: z.enum(['place', 'online', 'address']).catch('place').default('place'),
  customer_address: z.string().nullable().default(null),
  token_number: z.number().nullable().default(null),
  customer_name: z.string(),
  customer_email: z.string(),
  customer_phone: z.string().nullable().default(null),
  notes: z.string().nullable().default(null),
  answers: z
    .array(z.object({ question_id: z.string(), label: z.string(), value: z.union([z.string(), z.boolean()]) }))
    .default([]),
  created_at: z.string(),
  can_cancel: z.boolean().default(false),
  /** The shop's zone: the time was booked against its clock and is shown in it. */
  timezone: z.string().optional(),
  // --- Booking payments. All optional: absent on a shop without them (and on
  // a server or sample shop that predates them), which reads as nothing paid
  // and nothing owed.
  /** `FULL`, `DEPOSIT`, `PAY_AT_VENUE`, `CUSTOMER_CHOICE` or `PACKAGE`. */
  payment_mode: z.string().nullish(),
  /** `NOT_REQUIRED`, `AWAITING`, `PAID`, `DEPOSIT_PAID`, `PARTIALLY_REFUNDED`, `REFUNDED`... */
  payment_status: z.string().nullish(),
  price_total: moneyText.optional(),
  amount_paid: moneyText.optional(),
  amount_refunded: moneyText.optional(),
  /** When a slot held for payment goes back on sale (status `AWAITING_PAYMENT`). */
  hold_expires_at: z.string().nullish(),
  /** What cancelling now would refund - the shop's policy, worked out by the server. */
  refund_preview: moneyText.optional(),
  /** Lets "Complete payment" call the storefront's booking payment endpoint. */
  access_token: z.string().nullish(),
  package_purchase_id: z.string().nullish(),
});

/** `upcoming`: still on and not started. `past`: everything else. */
export type AppointmentRange = 'upcoming' | 'past' | 'all';

export type CustomerProfile = z.infer<typeof customerProfileSchema>;
export type AccountAppointment = z.infer<typeof accountAppointmentSchema>;
export type CustomerActiveSession = z.infer<typeof activeSessionSchema>;
export type SavedAddress = z.infer<typeof savedAddressSchema>;
export type AccountOrderSummary = z.infer<typeof accountOrderSummarySchema>;
export type AccountOrderDetail = z.infer<typeof accountOrderDetailSchema>;

export interface AddressPayload {
  name?: string;
  phone?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country?: string;
  is_default?: boolean;
}

// --- Session ---------------------------------------------------------------
export function registerCustomer(payload: {
  name: string;
  email: string;
  password: string;
  phone?: string;
  marketing_opt_in: boolean;
}) {
  return apiRequest(
    '/storefront/account/register',
    registerResultSchema,
    options({ method: 'POST', body: payload, skipAuthRefresh: true }),
  );
}

/** `captchaToken` once the API has asked for a Turnstile check. */
export function loginCustomer(email: string, password: string, captchaToken?: string) {
  return apiRequest(
    '/storefront/account/login',
    customerSessionSchema,
    options({
      method: 'POST',
      body: { email, password, captcha_token: captchaToken },
      skipAuthRefresh: true,
    }),
  );
}

export function logoutCustomer() {
  return apiRequest(
    '/storefront/account/logout',
    messageSchema,
    options({ method: 'POST', skipAuthRefresh: true }),
  );
}

export function fetchCustomerProfile() {
  return apiRequest('/storefront/account/me', customerProfileSchema, options());
}

export function updateCustomerProfile(payload: {
  name?: string;
  phone?: string | null;
  marketing_opt_in?: boolean;
  /** A language code, or null to follow the shop's own resolution. */
  preferred_language?: string | null;
}) {
  return apiRequest(
    '/storefront/account/me',
    customerProfileSchema,
    options({ method: 'PATCH', body: payload }),
  );
}

// --- Email and password ----------------------------------------------------
export function verifyCustomerEmail(token: string) {
  return apiRequest(
    '/storefront/account/verify-email',
    messageSchema,
    options({ method: 'POST', body: { token }, skipAuthRefresh: true }),
  );
}

export function resendCustomerVerification(email: string) {
  return apiRequest(
    '/storefront/account/resend-verification',
    messageSchema,
    options({ method: 'POST', body: { email }, skipAuthRefresh: true }),
  );
}

export function requestCustomerPasswordReset(email: string) {
  return apiRequest(
    '/storefront/account/forgot-password',
    messageSchema,
    options({ method: 'POST', body: { email }, skipAuthRefresh: true }),
  );
}

export function resetCustomerPassword(token: string, password: string) {
  return apiRequest(
    '/storefront/account/reset-password',
    messageSchema,
    options({ method: 'POST', body: { token, password }, skipAuthRefresh: true }),
  );
}

export function changeCustomerPassword(currentPassword: string, newPassword: string) {
  return apiRequest(
    '/storefront/account/change-password',
    messageSchema,
    options({
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),
  );
}

// --- Devices ---------------------------------------------------------------
export function fetchCustomerSessions() {
  return apiRequest('/storefront/account/sessions', z.array(activeSessionSchema), options());
}

export function revokeCustomerSession(sessionId: string) {
  return apiRequest(
    `/storefront/account/sessions/${sessionId}`,
    sessionRevocationSchema,
    options({ method: 'DELETE' }),
  );
}

export function revokeOtherCustomerSessions() {
  return apiRequest(
    '/storefront/account/sessions',
    sessionRevocationSchema,
    options({ method: 'DELETE' }),
  );
}

// --- Orders ----------------------------------------------------------------
export function fetchCustomerOrders(page = 1) {
  return apiRequest(
    `/storefront/account/orders?page=${page}`,
    pageSchema(accountOrderSummarySchema),
    options(),
  );
}

export function fetchCustomerOrder(orderId: string) {
  return apiRequest(
    `/storefront/account/orders/${orderId}`,
    accountOrderDetailSchema,
    options(),
  );
}

/** The order's GST invoice as a PDF, raised on first request. */
export function fetchCustomerInvoice(orderId: string) {
  return apiRequestBlob(`/storefront/account/orders/${orderId}/invoice`, options());
}

export function cancelCustomerOrder(orderId: string, reason?: string, comment?: string) {
  return apiRequest(
    `/storefront/account/orders/${orderId}/cancel`,
    accountOrderDetailSchema,
    options({
      method: 'POST',
      body: { reason: reason || undefined, comment: comment || undefined },
    }),
  );
}

// --- Appointments ----------------------------------------------------------
export function fetchCustomerAppointments(range: AppointmentRange = 'upcoming', page = 1) {
  return apiRequest(
    `/storefront/account/appointments?range=${range}&page=${page}`,
    pageSchema(accountAppointmentSchema),
    options(),
  );
}

export function fetchCustomerAppointment(appointmentId: string) {
  return apiRequest(
    `/storefront/account/appointments/${appointmentId}`,
    accountAppointmentSchema,
    options(),
  );
}

export function cancelCustomerAppointment(appointmentId: string) {
  return apiRequest(
    `/storefront/account/appointments/${appointmentId}/cancel`,
    accountAppointmentSchema,
    options({ method: 'POST' }),
  );
}

export interface OrderRequestPayload {
  kind: RequestKind;
  items: { order_item_id: string; quantity: number }[];
  reason: string;
  comment?: string;
  photos?: string[];
}

/** A return, refund, replacement or partial cancellation. */
export function createOrderRequest(orderId: string, payload: OrderRequestPayload) {
  return apiRequest(
    `/storefront/account/orders/${orderId}/requests`,
    accountOrderDetailSchema,
    options({ method: 'POST', body: payload }),
  );
}

/** A photo for a request; the URL it returns goes in the request's `photos`. */
export function uploadOrderRequestPhoto(orderId: string, file: File) {
  const body = new FormData();
  body.append('file', file);
  return apiRequest(
    `/storefront/account/orders/${orderId}/requests/photos`,
    z.object({ url: z.string() }),
    options({ method: 'POST', body }),
  );
}

export function reorder(orderId: string, cartToken: string | null) {
  const query = cartToken ? `?cart_token=${encodeURIComponent(cartToken)}` : '';
  return apiRequest(
    `/storefront/account/orders/${orderId}/reorder${query}`,
    reorderResultSchema,
    options({ method: 'POST' }),
  );
}

// --- Addresses -------------------------------------------------------------
export function fetchCustomerAddresses() {
  return apiRequest('/storefront/account/addresses', z.array(savedAddressSchema), options());
}

export function createCustomerAddress(payload: AddressPayload) {
  return apiRequest(
    '/storefront/account/addresses',
    savedAddressSchema,
    options({ method: 'POST', body: payload }),
  );
}

export function updateCustomerAddress(addressId: string, payload: AddressPayload) {
  return apiRequest(
    `/storefront/account/addresses/${addressId}`,
    savedAddressSchema,
    options({ method: 'PUT', body: payload }),
  );
}

export function setDefaultCustomerAddress(addressId: string) {
  return apiRequest(
    `/storefront/account/addresses/${addressId}/default`,
    savedAddressSchema,
    options({ method: 'POST' }),
  );
}

export function deleteCustomerAddress(addressId: string) {
  return apiRequest(
    `/storefront/account/addresses/${addressId}`,
    messageSchema,
    options({ method: 'DELETE' }),
  );
}

// --- What to buy next ------------------------------------------------------
export const buyAgainItemSchema = z.object({
  variant_id: z.string(),
  product_id: z.string(),
  name: z.string(),
  slug: z.string(),
  variant_label: z.string().nullable(),
  image_url: z.string().nullable(),
  price: z.string(),
  in_stock: z.boolean(),
  last_ordered_at: z.string(),
});

/**
 * A suggested product. The catalog's own listing card, plus the variant to add
 * when the product is sold in one form only - null the moment there is a size
 * to pick, because that choice is the product page's to offer.
 */
export const accountRecommendationSchema = productSummarySchema.extend({
  variant_id: z.string().nullable(),
});

export type BuyAgainItem = z.infer<typeof buyAgainItemSchema>;
export type AccountRecommendation = z.infer<typeof accountRecommendationSchema>;

export function fetchBuyAgain(limit = 12) {
  return apiRequest(
    `/storefront/account/buy-again?limit=${limit}`,
    z.array(buyAgainItemSchema),
    options(),
  );
}

export function fetchAccountRecommendations(limit = 12) {
  return apiRequest(
    `/storefront/account/recommendations?limit=${limit}`,
    z.array(accountRecommendationSchema),
    options(),
  );
}

// --- Wishlist --------------------------------------------------------------
/** A saved product: the catalog's listing card, plus when it was saved. */
export const wishlistProductSchema = productSummarySchema.extend({
  added_at: z.string(),
});

export type WishlistProduct = z.infer<typeof wishlistProductSchema>;

/** The shopper's saved products, newest first. Every write answers with the whole list. */
export function fetchWishlist() {
  return apiRequest('/storefront/account/wishlist', z.array(wishlistProductSchema), options());
}

export function addToWishlist(productId: string) {
  return apiRequest(
    `/storefront/account/wishlist/${encodeURIComponent(productId)}`,
    z.array(wishlistProductSchema),
    options({ method: 'PUT' }),
  );
}

export function removeFromWishlist(productId: string) {
  return apiRequest(
    `/storefront/account/wishlist/${encodeURIComponent(productId)}`,
    z.array(wishlistProductSchema),
    options({ method: 'DELETE' }),
  );
}

/** A guest's hearts, brought into the account once they sign in. */
export function mergeWishlist(productIds: string[]) {
  return apiRequest(
    '/storefront/account/wishlist/merge',
    z.array(wishlistProductSchema),
    options({ method: 'POST', body: { product_ids: productIds } }),
  );
}
